import * as PIXI from 'pixi.js';
import { ColorMapFilter } from 'pixi-filters';
import { Config } from '../../video-data/config';
import type { BaseSettings } from '../TimeAwareBase';
import { getGlobalAssetNetworkManager } from '../AssetNetworkManager';
import { FETCH_ON_BUFFER } from '../../types';
import { Filter } from './Filter';

const DEFAULT_COLOR_MAP = PIXI.Texture.EMPTY;
const DEFAULT_FILTER_INTENSITY = 0.0;

export class ColorGradingFilter extends Filter {
    override _classType = Config.COLOR_GRADING_FILTER;
    override pixiFilter: ColorMapFilter;

    #textureUrl: string = '';
    #textureUrlPendingLoad: string | null = null;
    #textureLoadFailed: boolean = false;
    #shouldLoad: boolean = false;
    #texture: PIXI.Texture = DEFAULT_COLOR_MAP;
    #intensity: number = DEFAULT_FILTER_INTENSITY;

    static _textureCache: Map<string, PIXI.Texture> = new Map();

    constructor(settings: BaseSettings) {
        super(settings);
        if (typeof settings.fetchResources === 'boolean') {
            this.#shouldLoad = settings.fetchResources;
        } else if (
            settings.fetchResources.media === true ||
            settings.fetchResources.media === FETCH_ON_BUFFER
        ) {
            this.#shouldLoad = true;
        }
        this.pixiFilter = new ColorMapFilter(DEFAULT_COLOR_MAP, false, DEFAULT_FILTER_INTENSITY);
    }

    #setTexture(texture: PIXI.Texture, textureUrl: string): void {
        if (
            textureUrl &&
            this.#textureUrlPendingLoad &&
            this.#textureUrlPendingLoad !== textureUrl
        ) {
            // Skip apply if the texture URL is different from the one that is pending load
            return;
        }
        this.#textureUrlPendingLoad = null;
        this.#textureUrl = textureUrl;
        this.#texture = texture;
    }

    #resetTextureProperties(): void {
        this.#setTexture(DEFAULT_COLOR_MAP, '');
        this.#intensity = DEFAULT_FILTER_INTENSITY;
    }

    #onTextureLoadSuccess(
        texture: PIXI.Texture,
        textureUrl: string,
        storeInCache: boolean = true,
    ): void {
        this.#textureLoadFailed = false;
        this.#setTexture(texture, textureUrl);
        if (storeInCache) {
            ColorGradingFilter._textureCache.set(textureUrl, texture);
        }
        this.emit('onLoaded');
    }

    #onTextureLoadFailed(): void {
        this.emit('onLoaded');
        this.#textureLoadFailed = true;
        this.#resetTextureProperties();
    }

    /**
     * Load the texture from the given URL and apply it to the filter.
     * If the URL is empty, use the default color map.
     * If the texture is already in the cache, use it directly.
     * @param textureUrl The URL of the texture to load.
     */
    async #loadTexture(textureUrl: string): Promise<void> {
        if (!textureUrl) {
            this.#resetTextureProperties();
            return;
        } else if (ColorGradingFilter._textureCache.has(textureUrl)) {
            // Use texture that is already in the cache
            const texture: PIXI.Texture = ColorGradingFilter._textureCache.get(textureUrl);
            if (texture.baseTexture?.valid) {
                this.#onTextureLoadSuccess(texture, textureUrl, false);
                return;
            }
        }
        return new Promise((resolve, reject) => {
            const networkManager = getGlobalAssetNetworkManager();
            networkManager.waitForNetworkSlot(this, (requestFinished) => {
                const texture = PIXI.Texture.from(textureUrl);
                // Check if valid texture already exists
                if (texture.baseTexture?.valid) {
                    this.#onTextureLoadSuccess(texture, textureUrl);
                    requestFinished();
                    resolve();
                    return;
                }

                texture.baseTexture.on('loaded', () => {
                    this.#onTextureLoadSuccess(texture, textureUrl);
                    requestFinished();
                    resolve();
                });
                texture.baseTexture.on('error', (error) => {
                    this.#onTextureLoadFailed();
                    requestFinished();
                    reject(error);
                });
            });
        });
    }

    set textureUrl(value: string) {
        if (this.#textureUrl === value) {
            return;
        }
        if (this.#textureUrlPendingLoad === value) {
            return;
        }
        if (!this.#shouldLoad) {
            return;
        }
        if (this.#textureUrlPendingLoad) {
            // If a texture is already loading, we will overwrite it with the new one
            console.warn('Ongoing texture load will be replaced by new texture');
        }
        this.#textureUrlPendingLoad = value;
        this.#loadTexture(value)
            .catch((error) => {
                console.error('Error loading texture:', error);
                throw error;
            })
            .finally(() => {
                this.#textureUrlPendingLoad = null;
                this.requiresUpdate = true;
            });
    }

    get textureUrl(): string {
        return this.#textureUrl;
    }

    set intensity(value: number) {
        this.#intensity = value;
        this.requiresUpdate = true;
    }

    get intensity(): number {
        return this.#intensity;
    }

    get isLoaded(): boolean {
        return this.#textureUrlPendingLoad === null;
    }

    get failedToLoad(): boolean {
        return this.#textureLoadFailed;
    }

    override _update() {
        if (
            this.#textureUrlPendingLoad === null &&
            (this.#texture.baseTexture?.valid || this.#texture === DEFAULT_COLOR_MAP)
        ) {
            this.pixiFilter.colorMap = this.#texture;
            this.pixiFilter.mix = this.intensity;
        }
    }

    override destroy(): void {
        // Detatch instead of destroying the texture so that reference can be reused
        this.pixiFilter.colorMap = null;
    }

    override isNeutral(): boolean {
        return this.#textureUrl === '' && this.#intensity === DEFAULT_FILTER_INTENSITY;
    }
}
